import {
  Box,
  Button,
  createStyles,
  Grid,
  Group,
  Image,
  Skeleton,
  useMantineTheme
} from '@mantine/core'
import { useForm, yupResolver } from '@mantine/form'
import { useMediaQuery } from '@mantine/hooks'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { useSWRConfig } from 'swr'

import { api, errorHandler, isValidCPF, notificationHandler, Yup } from '@/lib/utils'
import { useRouterLoading } from '@/providers/RouterLoadingProvider'

import * as ProfileFormFields from './Fields'

interface Props {
  userData: any
  isSitePrivate: boolean
  mode?: 'default' | 'register-validation'
}

interface FormValues {
  email: string
  firstName: string
  lastName: string
  taxDocument?: string
  phoneNumber?: string
  birthdate?: string
  password?: string
  confirmPassword?: string
  oldPassword?: string
}

const useStyles = createStyles(() => ({
  pictureBox: {
    border: '1px solid #CCCCCC',
    height: '190px',
    margin: 'auto',
    maxWidth: '100%',
    overflow: 'hidden',
    position: 'relative',
    width: '190px'
  }
}))

export default function Profile({ userData, isSitePrivate = false }: Props) {
  // Hooks
  const router = useRouter()
  const { isLoading: isLoadingGlobal } = useRouterLoading()
  const { mutate: mutateGlobal } = useSWRConfig()
  const theme = useMantineTheme()
  const isXs = useMediaQuery(`(max-width: ${theme.breakpoints.xs}px)`)
  const { classes } = useStyles()
  const taxDocumentRegex = /^(\d{3}\.?\d{3}\.?\d{3}-?\d{2})/
  const today = new Date()
  const minDate = new Date('1950-01-01').toISOString()
  const maxDate = today.toISOString()

  // States
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [picture, setPicture] = useState<string>(userData?.picture || '')

  // Constants
  const { site: siteSlug } = router.query || {}
  const initialValues = {
    email: userData?.email || '',
    firstName: userData?.firstName || '',
    lastName: userData?.lastName || '',
    taxDocument: userData?.taxDocument || '',
    phoneNumber: userData?.phoneNumber || '',
    birthdate: userData?.birthdate ? userData.birthdate.split('T')[0] : '',
    password: '',
    confirmPassword: '',
    oldPassword: ''
  }

  const schema = Yup.object().shape({
    email: Yup.string().email().required(),
    firstName: Yup.string().min(2).max(50).required(),
    lastName: Yup.string().min(2).max(150).required(),
    phoneNumber: Yup.string(),
    taxDocument: Yup.string()
      .matches(taxDocumentRegex, 'Digite um CPF válido')
      .test('valid-cpf', 'Digite um CPF válido', value => !value || isValidCPF(value)),
    birthdate: Yup.date()
      .nullable()
      .min(minDate, 'Data deve ser maior que 01/01/1950')
      .max(maxDate, 'Data deve ser menor que hoje')
      .typeError('Data inválida'),
    password: Yup.string(),
    confirmPassword: Yup.string().oneOf([Yup.ref('password'), null], 'Senhas diferentes'),
    oldPassword: Yup.string().when('password', {
      is: (password: string) => password.length > 0,
      then: Yup.string().required('Senha antiga obrigatória')
    })
  })

  // Mantine form
  const form = useForm({
    initialValues,
    validate: yupResolver(schema),
    validateInputOnBlur: true,
    validateInputOnChange: true
  })

  // Actions
  const patchProfile = (data: any, successMessage: string) => {
    const { birthdate, ...restData } = data
    api
      .patch(`/${siteSlug}/accounts/me/`, {
        user: { ...restData, ...(birthdate ? { birthdate } : {}) }
      })
      .then(response => {
        mutateGlobal(`/${siteSlug}/accounts/me/`)
        notificationHandler({ variant: 'success', message: successMessage })
        return response?.data?.uid
      })
      .catch(err => {
        const errors = errorHandler(err?.response?.data)
        notificationHandler({
          variant: 'error',
          message: `${errors?.messages?.[0]}` || 'Houve um erro ao tentar atualizar os dados'
        })
      })
      .finally(() => setIsSubmitting(false))
  }

  const handleFileUpload = (payload: File[]) => {
    if (payload?.length > 1) return null

    const filteredFiles = payload.find(
      (file, index, array) =>
        file?.size < 5242880 && index === array.findIndex(fItem => fItem.name === file.name)
    )

    if (filteredFiles) {
      const reader = new FileReader()
      reader.onloadend = () => {
        patchProfile({ picture: reader.result }, 'Foto atualizada com sucesso')
      }
      reader.readAsDataURL(filteredFiles)
      setPicture(filteredFiles ? URL.createObjectURL(filteredFiles) : '')
    }
  }

  const handleSubmit = async (newValues: FormValues) => {
    setIsSubmitting(true)

    const newProfile = {
      email: newValues.email,
      first_name: newValues.firstName,
      last_name: newValues.lastName,
      tax_document: newValues.taxDocument,
      phone_number: newValues.phoneNumber,
      birthdate: newValues.birthdate
    }

    if (form.isDirty()) {
      patchProfile(newProfile, 'Dados atualizados com sucesso')
    }

    if (newValues.password) {
      api
        .post(`/${siteSlug}/password/`, {
          oldPassword: newValues.oldPassword,
          newPassword: newValues.password
        })
        .then(() => {
          notificationHandler({ variant: 'success', message: 'Senha atualizada com sucesso' })
          router.reload()
        })
        .catch(errorHandler)
        .finally(() => setIsSubmitting(false))
    }
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Grid>
        <Grid.Col span={12} xs={4} sm={3} md={2}>
          <Box mb="xl" className={classes.pictureBox}>
            {picture ? (
              <Image alt="Foto" src={picture} />
            ) : (
              <Skeleton height={190} animate={false} />
            )}
          </Box>
          <ProfileFormFields.Picture inputProps={{ onChange: handleFileUpload }} />
        </Grid.Col>
        <Grid.Col span={12} xs={8} sm={9} md={10}>
          <Grid>
            <Grid.Col span={12} md={6}>
              <ProfileFormFields.Email
                inputProps={{
                  ...form.getInputProps('email'),
                  disabled: isSubmitting || isSitePrivate
                }}
              />
            </Grid.Col>
            <Grid.Col span={12} md={6}>
              <ProfileFormFields.FirstName
                inputProps={{
                  ...form.getInputProps('firstName'),
                  disabled: isSubmitting || isSitePrivate
                }}
              />
            </Grid.Col>
            <Grid.Col span={12} md={6}>
              <ProfileFormFields.LastName
                inputProps={{
                  ...form.getInputProps('lastName'),
                  disabled: isSubmitting || isSitePrivate
                }}
              />
            </Grid.Col>
            {/* <Grid.Col span={12} md={6}>
              <ProfileFormFields.PhoneNumber
                inputProps={{
                  ...form.getInputProps('phoneNumber'),
                  disabled: isSubmitting || isSitePrivate
                }}
              />
            </Grid.Col>
            <Grid.Col span={12} md={6}>
              <ProfileFormFields.TaxDocument
                inputProps={{
                  ...form.getInputProps('taxDocument'),
                  disabled: isSubmitting || isSitePrivate
                }}
              />
            </Grid.Col> */}
            <Grid.Col span={12} md={6}>
              <ProfileFormFields.Birthdate
                inputProps={{
                  ...form.getInputProps('birthdate'),
                  disabled: isSubmitting || isSitePrivate,
                  type: 'date',
                  label: 'Data de nascimento',
                  placeholder: 'Data de nascimento'
                }}
              />
            </Grid.Col>
            <Grid.Col span={12} md={6}>
              <ProfileFormFields.Password
                inputProps={{
                  ...form.getInputProps('password'),
                  disabled: isSubmitting
                }}
              />
            </Grid.Col>
            <Grid.Col span={12} md={6}>
              <ProfileFormFields.ConfirmPassword
                inputProps={{
                  ...form.getInputProps('confirmPassword'),
                  disabled: isSubmitting
                }}
              />
            </Grid.Col>
            <Grid.Col span={12} md={6}>
              <ProfileFormFields.OldPassword
                inputProps={{
                  ...form.getInputProps('oldPassword'),
                  disabled: isSubmitting
                }}
              />
            </Grid.Col>
          </Grid>
          <Group position="right" mt="xl">
            <Button
              type="submit"
              fullWidth={!!isXs}
              disabled={!form.isValid() || !form.isDirty() || isLoadingGlobal}
              loading={isSubmitting}>
              Salvar
            </Button>
          </Group>
        </Grid.Col>
      </Grid>
    </form>
  )
}
